"use client";

import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Copy,
  Loader2,
  Plus,
  Power,
  RefreshCw,
  Trash2,
  Webhook,
  Zap,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import type { Workspace } from "@/lib/types";
import { formatRelative } from "@/lib/utils";

// Live = wired into a fire path. Coming-soon = enum value exists but no
// app code dispatches it yet. Render coming-soon ones grayed out for
// transparency rather than hiding them.
const LIVE_EVENTS = [
  { id: "due_in_7d", label: "Due in 7 days", description: "Fires when a task crosses the 7-day-out threshold" },
  { id: "due_in_1d", label: "Due in 1 day", description: "T-1d reminder" },
  { id: "due_now", label: "Due today", description: "Within ±24h of due_date" },
  { id: "overdue", label: "Overdue", description: "Past due_date and still open" },
  { id: "comment_mentioned", label: "Comment @mention", description: "Fires when a non-bot comment mentions a workspace bot — agent-trigger surface (PAUT-30)" },
] as const;
const COMING_SOON_EVENTS = [
  { id: "issue_created", label: "Task created (coming soon)", description: "" },
  { id: "issue_status_change", label: "Task status change (coming soon)", description: "" },
  { id: "progress_card_published", label: "Progress card published (coming soon)", description: "" },
] as const;

interface WebhookRow {
  id: string;
  url: string;
  events: string[];
  description: string;
  enabled: boolean;
  created_at: string;
  last_delivery_at: string | null;
  last_delivery_status: number | null;
  failure_count: number;
}

export function WebhooksModal({
  workspace,
  open,
  onClose,
}: {
  workspace: Workspace;
  open: boolean;
  onClose: () => void;
}) {
  const [hooks, setHooks] = useState<WebhookRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newEvents, setNewEvents] = useState<string[]>([
    "due_in_7d",
    "due_in_1d",
    "due_now",
    "overdue",
  ]);
  const [freshSecret, setFreshSecret] = useState<{ url: string; secret: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const res = await fetch(`/api/workspaces/${workspace.id}/webhooks`);
    const data = await res.json();
    setHooks(data.webhooks ?? []);
    setLoading(false);
  }

  useEffect(() => {
    if (open) {
      void load();
      setFreshSecret(null);
      setShowNew(false);
      setNewUrl("");
      setNewDescription("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspace.id]);

  async function handleCreate() {
    if (!newUrl.trim()) return;
    setCreating(true);
    const res = await fetch(`/api/workspaces/${workspace.id}/webhooks`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        url: newUrl.trim(),
        events: newEvents,
        description: newDescription.trim(),
      }),
    });
    const data = await res.json();
    setCreating(false);
    if (data.ok) {
      setFreshSecret({ url: data.webhook.url, secret: data.secret });
      setShowNew(false);
      setNewUrl("");
      setNewDescription("");
      void load();
    } else {
      alert(`Error: ${data.error}`);
    }
  }

  async function handleToggle(wh: WebhookRow) {
    await fetch(`/api/workspaces/${workspace.id}/webhooks/${wh.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ enabled: !wh.enabled }),
    });
    void load();
  }

  async function handleDelete(wh: WebhookRow) {
    if (!confirm(`Delete webhook to ${wh.url}? Pending events will not be delivered.`))
      return;
    await fetch(`/api/workspaces/${workspace.id}/webhooks/${wh.id}`, { method: "DELETE" });
    void load();
  }

  async function handleTest(wh: WebhookRow) {
    setTesting(wh.id);
    const res = await fetch(`/api/workspaces/${workspace.id}/webhooks/${wh.id}`, {
      method: "POST",
    });
    const data = await res.json();
    setTesting(null);
    if (data.ok) {
      alert(`Test event delivered. Status: ${data.status}`);
    } else {
      alert(`Test failed. Status: ${data.status ?? "—"}\n${data.error ?? ""}`);
    }
    void load();
  }

  return (
    <Modal open={open} onClose={onClose} width={780}>
      <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--border)]">
        <Webhook size={14} className="text-[var(--accent)]" />
        <h3 className="text-[13.5px] font-semibold">Webhooks</h3>
        <span className="text-[11.5px] text-[var(--fg-tertiary)]">·</span>
        <span className="text-[11.5px] text-[var(--fg-tertiary)]">{workspace.name}</span>
        <button
          onClick={() => void load()}
          className="ml-auto w-7 h-7 rounded-md hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)]"
          title="Refresh"
        >
          <RefreshCw size={12} />
        </button>
      </div>

      {freshSecret && (
        <div className="mx-5 mt-4 rounded-md border border-[var(--green)]/30 bg-[var(--green)]/8 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-[var(--green)] mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[var(--fg)]">
                Webhook created — copy the secret now
              </div>
              <p className="text-[11.5px] text-[var(--fg-tertiary)] mt-0.5">
                Use this to verify the <code>x-pauta-signature</code> header on the receiving
                end (HMAC-SHA256). We will never show it again.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 text-[12px] font-mono bg-[var(--bg-tertiary)] border border-[var(--border)] rounded px-2 py-1.5 text-[var(--fg)] truncate">
                  {freshSecret.secret}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(freshSecret.secret);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[11.5px] font-medium"
                >
                  <Copy size={12} />
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
              <p className="text-[10.5px] text-[var(--fg-muted)] mt-2 truncate">
                Endpoint: <span className="font-mono">{freshSecret.url}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="px-5 pt-4">
        {showNew ? (
          <div className="rounded-md border border-[var(--border-strong)] bg-[var(--bg-secondary)] p-3 space-y-2">
            <input
              autoFocus
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://your-service.example.com/api/webhooks/pauta"
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)] font-mono"
            />
            <input
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Description (e.g. Box → WhatsApp dispatcher)"
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[12.5px] outline-none focus:border-[var(--accent)]"
            />
            <div>
              <div className="text-[10.5px] uppercase tracking-wide text-[var(--fg-muted)] font-medium mb-1">
                Subscribe to events
              </div>
              <div className="grid grid-cols-2 gap-1">
                {LIVE_EVENTS.map((e) => (
                  <label
                    key={e.id}
                    className="flex items-center gap-1.5 text-[11.5px] cursor-pointer"
                    title={e.description}
                  >
                    <input
                      type="checkbox"
                      checked={newEvents.includes(e.id)}
                      onChange={(ev) =>
                        setNewEvents((prev) =>
                          ev.target.checked ? [...prev, e.id] : prev.filter((x) => x !== e.id)
                        )
                      }
                      className="accent-[var(--accent)]"
                    />
                    <span className="font-mono">{e.id}</span>
                  </label>
                ))}
                {COMING_SOON_EVENTS.map((e) => (
                  <div
                    key={e.id}
                    className="flex items-center gap-1.5 text-[11.5px] opacity-40 cursor-not-allowed"
                    title="Not wired yet"
                  >
                    <input type="checkbox" disabled className="accent-[var(--accent)]" />
                    <span className="font-mono">{e.id}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setShowNew(false);
                  setNewUrl("");
                }}
                className="px-2.5 py-1 text-[12px] text-[var(--fg-tertiary)] hover:text-[var(--fg-secondary)]"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newUrl.trim() || creating}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-50 text-white text-[12px] font-medium"
              >
                {creating ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                {creating ? "Creating…" : "Add webhook"}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowNew(true)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-dashed border-[var(--border-strong)] text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] hover:border-[var(--accent)] transition-colors"
          >
            <Plus size={13} />
            Add webhook
          </button>
        )}
      </div>

      <div className="px-5 py-4 max-h-[400px] overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-[12.5px] text-[var(--fg-muted)]">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading webhooks…
          </div>
        ) : hooks.length === 0 ? (
          <div className="text-center py-12 text-[12.5px] text-[var(--fg-muted)]">
            No webhooks yet. Add one to forward task events to Box, Slack, or any HTTP
            endpoint.
          </div>
        ) : (
          <div className="space-y-2">
            {hooks.map((wh) => (
              <WebhookRow
                key={wh.id}
                webhook={wh}
                testing={testing === wh.id}
                onToggle={() => handleToggle(wh)}
                onDelete={() => handleDelete(wh)}
                onTest={() => handleTest(wh)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-[var(--border)] bg-[var(--bg-secondary)] text-[11px] text-[var(--fg-muted)]">
        Cron scans due dates hourly. Each delivery includes{" "}
        <code>x-pauta-signature: sha256=&lt;hmac&gt;</code> — verify with the secret.
      </div>
    </Modal>
  );
}

function WebhookRow({
  webhook,
  testing,
  onToggle,
  onDelete,
  onTest,
}: {
  webhook: WebhookRow;
  testing: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onTest: () => void;
}) {
  const lastOk =
    webhook.last_delivery_status != null && webhook.last_delivery_status < 400;
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-3">
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={onToggle}
          className={`w-7 h-5 rounded-full flex items-center transition-colors ${
            webhook.enabled
              ? "bg-[var(--accent)] justify-end"
              : "bg-[var(--bg-tertiary)] justify-start"
          }`}
          title={webhook.enabled ? "Disable" : "Enable"}
        >
          <span className="w-4 h-4 rounded-full bg-white mx-0.5" />
        </button>
        <Webhook size={13} className="text-[var(--fg-tertiary)]" />
        <code className="text-[12px] font-mono truncate flex-1">{webhook.url}</code>
        <button
          onClick={onTest}
          disabled={testing}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-50"
          title="Send test event"
        >
          {testing ? (
            <Loader2 size={10} className="animate-spin" />
          ) : (
            <Zap size={10} />
          )}
          {testing ? "Testing…" : "Test"}
        </button>
        <button
          onClick={onDelete}
          className="w-6 h-6 flex items-center justify-center rounded text-[var(--fg-tertiary)] hover:text-[var(--red)] hover:bg-[var(--bg-hover)]"
          title="Delete"
        >
          <Trash2 size={11} />
        </button>
      </div>

      {webhook.description && (
        <div className="text-[11.5px] text-[var(--fg-tertiary)] mb-2 truncate">
          {webhook.description}
        </div>
      )}

      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
        {webhook.events.map((e) => (
          <span
            key={e}
            className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--fg-tertiary)]"
          >
            {e}
          </span>
        ))}
      </div>

      <div className="flex items-center gap-2 text-[10.5px] text-[var(--fg-muted)]">
        {webhook.last_delivery_at ? (
          <>
            {lastOk ? (
              <CheckCircle2 size={10} className="text-[var(--green)]" />
            ) : (
              <AlertCircle size={10} className="text-[var(--red)]" />
            )}
            Last delivery: {formatRelative(Date.parse(webhook.last_delivery_at))} (HTTP{" "}
            {webhook.last_delivery_status ?? "—"})
          </>
        ) : (
          <>
            <Power size={10} /> No deliveries yet
          </>
        )}
        {webhook.failure_count > 0 && (
          <>
            <span>·</span>
            <span className="text-[var(--orange)]">{webhook.failure_count} consecutive failures</span>
          </>
        )}
      </div>
    </div>
  );
}
